import { useI18n } from 'vue-i18n';
import {
  getLoadTypeDirectionDisplay,
  LoadTypeDirection,
  LoadTypeDirectionDisplay
} from '@nova/core';
import { usePreparationAppointmentsFeature } from '@satellite/composables/usePreparationAppointmentsFeature';
import { useLoadTypeDirectionLabel } from '@satellite/composables/useLoadTypeDirectionLabel';

import type { LoadTypeDirectionSubject, Maybe } from '@nova/core';

type DirectionLoadType = Maybe<LoadTypeDirectionSubject>;

const TILE_LABEL_KEYS: Partial<Record<LoadTypeDirectionDisplay, string>> = {
  [LoadTypeDirectionDisplay.Inbound]: 'grid.events.loadTypeDirections.Inbound',
  [LoadTypeDirectionDisplay.Outbound]: 'grid.events.loadTypeDirections.Outbound',
  [LoadTypeDirectionDisplay.InboundAndOutbound]: 'grid.events.loadTypeDirections.InOut',
  [LoadTypeDirectionDisplay.DropOff]: 'grid.events.loadTypeDirections.DropOff',
  [LoadTypeDirectionDisplay.PickUp]: 'grid.events.loadTypeDirections.PickUp',
  [LoadTypeDirectionDisplay.DropAndHook]: 'grid.events.loadTypeDirections.DropAndHook'
};

const LEGACY_TILE_LABEL_KEYS: Partial<Record<string, string>> = {
  [LoadTypeDirection.Inbound]: 'grid.events.loadTypeDirections.Inbound',
  [LoadTypeDirection.Outbound]: 'grid.events.loadTypeDirections.Outbound',
  [LoadTypeDirection.InOut]: 'grid.events.loadTypeDirections.InOut'
};

export function useLoadTypeDirectionDisplay() {
  const { t } = useI18n();
  const { isPreparationAppointmentsEnabled } = usePreparationAppointmentsFeature();
  const { getDirectionLabel } = useLoadTypeDirectionLabel();

  function getDirectionText(loadType: DirectionLoadType): string | undefined {
    return getDirectionLabel(loadType) ?? loadType?.direction ?? undefined;
  }

  // For titles that always showed the stored direction in upper case: the translated label as it
  // is while the flag is on, the upper-cased stored direction otherwise.
  function getDirectionTitle(loadType: DirectionLoadType): string | undefined {
    return getDirectionLabel(loadType) ?? loadType?.direction?.toUpperCase();
  }

  function getTileDirectionLabel(loadType: DirectionLoadType): string {
    const display = isPreparationAppointmentsEnabled.value
      ? getLoadTypeDirectionDisplay(loadType)
      : null;
    const tileLabelKey = isPreparationAppointmentsEnabled.value
      ? display && TILE_LABEL_KEYS[display]
      : LEGACY_TILE_LABEL_KEYS[loadType?.direction as string];

    return tileLabelKey ? t(tileLabelKey) : '';
  }

  // Only while the flag is on: the legacy tile draws the arrow from the label text.
  function getTileDirectionArrow(loadType: DirectionLoadType): 'in' | 'out' | undefined {
    if (!isPreparationAppointmentsEnabled.value) {
      return undefined;
    }

    if (loadType?.direction === LoadTypeDirection.Inbound) {
      return 'in';
    }

    return loadType?.direction === LoadTypeDirection.Outbound ? 'out' : undefined;
  }

  return { getDirectionText, getDirectionTitle, getTileDirectionLabel, getTileDirectionArrow };
}
